import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonRelationships.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonRelationships.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { PersonRelationshipViewModel, PersonRelationshipCreateCommand, PersonRelationshipUpdateCommand } from '../../models/Entities/Person/Relationship/PersonRelationshipModels';

export type KnownAction =
    | Actions.PersonRelationships.PersonRelationshipGetAllRequestAction
    | Actions.PersonRelationships.PersonRelationshipGetAllSuccessAction
    | Actions.PersonRelationships.PersonRelationshipGetAllFailureAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdRequestAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdSuccessAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdFailureAction
    | Actions.PersonRelationships.PersonRelationshipCreateRequestAction
    | Actions.PersonRelationships.PersonRelationshipCreateSuccessAction
    | Actions.PersonRelationships.PersonRelationshipCreateFailureAction
    | Actions.PersonRelationships.PersonRelationshipUpdateRequestAction
    | Actions.PersonRelationships.PersonRelationshipUpdateSuccessAction
    | Actions.PersonRelationships.PersonRelationshipUpdateFailureAction
    | Actions.PersonRelationships.PersonRelationshipDeleteRequestAction
    | Actions.PersonRelationships.PersonRelationshipDeleteSuccessAction
    | Actions.PersonRelationships.PersonRelationshipDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetPersonRelationships: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETALL_REQUEST, personId });

        Services.GetPersonRelationships(token, personId)
            .then((response: PersonRelationshipViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_RELATIONSHIP_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: PersonRelationshipViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETALL_FAILURE, error });
            });
    },

    GetPersonRelationshipById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETBYID_REQUEST, id });

        Services.GetPersonRelationshipById(token, id)
            .then((response: PersonRelationshipViewModel) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_GETBYID_FAILURE, error });
            });
    },

    CreatePersonRelationship: (payload: PersonRelationshipCreateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_CREATE_REQUEST, payload });

        Services.CreatePersonRelationship(token, payload)
            .then((created: PersonRelationshipViewModel) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_CREATE_FAILURE, error });
            });
    },

    UpdatePersonRelationship: (payload: PersonRelationshipUpdateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_UPDATE_REQUEST, payload });

        Services.UpdatePersonRelationship(token, payload)
            .then((updated: PersonRelationshipViewModel) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeletePersonRelationship: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_DELETE_REQUEST, id });

        Services.DeletePersonRelationship(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_RELATIONSHIP_DELETE_FAILURE, id, error });
            });
    }
};
